/**
 * The rules of the family onboarding, as plain functions: which screens apply, what each one
 * refuses, and the payload the server receives. The page and the tests share them.
 */
import { ApiError } from "../../Error/ApiErrors";

export type OnboardingMode = "new" | "google" | "existing";
export type OnboardingWho = "parent" | "child";

export type TBirthDate = { day: string; month: string; year: string };

export type TOnboardingAnswers = {
  email: string;
  birthDate: TBirthDate;
  consent: boolean;
  password: string;
  passwordConfirm: string;
  who: OnboardingWho | null;
  parentName: string;
  kidCount: number;
  kidNames: string[];
  pin: string;
  /** The child path: the avatar picked, then the child's first name. */
  avatar: string | null;
  childName: string;
};

export type TOnboardingLimits = {
  legalAge: number;
  maxChildren: number;
  minPassword: number;
};

/** What the page keeps the server from refusing; the server's own key wins when it does. */
export type OnboardingError =
  | "email"
  | "birth-date"
  | "under-age"
  | "terms"
  | "password-short"
  | "password-mismatch"
  | "name-length"
  | "name-taken"
  | "children-count"
  | "avatar"
  | "pin"
  | "pin-mismatch";

export type TLegalConsent = {
  birthDate: string;
  acceptedTos: true;
  acceptedPrivacy: true;
};

/** What creates the account once the password is typed: the account and the consent, with the human check. */
export type TAccountPayload = {
  account: { email: string; password: string };
  legal: TLegalConsent;
  captcha?: string;
};

/**
 * What a school account answers to « On se connaît déjà! » on the login popup. The server keeps it
 * from there to the end of the family onboarding: the page never carries it.
 */
export type TVersionChoice = "scolaire" | "famille";

/** The v2 contract of the handoff, as the server reads it at the end. The account exists by then. */
export type TOnboardingPayload = {
  startedBy: OnboardingWho;
  account: { email: string; authProvider: "password" | "google" };
  legal?: TLegalConsent;
  parent?: { name: string; pin: string };
  children: { name: string; avatar?: string }[];
  meta: { completedAt: string; locale: string };
};

/**
 * Where the page is, as the account keeps it: the screen, the way back, and the answers that
 * are not secrets. The confirmation email's link lands the parent back here.
 */
export type TServerProgress = {
  current: string;
  history: string[];
  answers: {
    who: OnboardingWho | null;
    parentName: string;
    kidCount: number;
    kidNames: string[];
    avatar: string | null;
    childName: string;
  };
};

const PARENT_STEPS = ["parentName", "kidCount", "kidNames", "pin"] as const;
/** The child names itself only: no PIN, the parent's profile comes later. */
const CHILD_STEPS = ["avatar", "childName"] as const;

export function emptyAnswers(email: string): TOnboardingAnswers {
  return {
    email,
    birthDate: { day: "", month: "", year: "" },
    consent: false,
    password: "",
    passwordConfirm: "",
    who: null,
    parentName: "",
    kidCount: 0,
    kidNames: [],
    pin: "",
    avatar: null,
    childName: "",
  };
}

/**
 * The screens in order for this visit. The email is asked only when nothing brought it; the
 * legal consent only when the account is new to us; the password only when Google is not the
 * way in. The path after « Qui veut continuer? » is known once the answer is.
 */
export function stepsFor(
  mode: OnboardingMode,
  emailKnown: boolean,
  who: OnboardingWho | null,
): string[] {
  const steps: string[] = [];
  if (mode === "new" && !emailKnown) {
    steps.push("email");
  }
  if (mode !== "existing") {
    steps.push("terms");
  }
  if (mode === "new") {
    steps.push("password");
  }
  steps.push("who");
  if (who === "parent") {
    steps.push(...PARENT_STEPS);
  } else if (who === "child") {
    steps.push(...CHILD_STEPS);
  }
  return steps;
}

export function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/** Characters as the server counts them (code points), not UTF-16 units: one emoji is one. */
export function charCount(text: string): number {
  return Array.from(text).length;
}

export function hasValidNameLength(name: string): boolean {
  const length = charCount(name.trim());
  return length >= 2 && length <= 20;
}

/** A refused field, as the server answers it: the message is already in the page's language. */
export type TRefusedField = { field: string; message: string };

/** The field the server refused, when the failure is one; null for anything else. */
export function refusedField(error: unknown): TRefusedField | null {
  const body = error instanceof ApiError ? error.responseData : null;
  if (body !== null && typeof body === "object") {
    const { field, message } = body as Partial<TRefusedField>;
    if (typeof field === "string" && typeof message === "string") {
      return { field, message };
    }
  }
  return null;
}

/**
 * What the server said about a failure, in the page's language; null when it said nothing (a
 * dropped connection, a proxy's error page), which leaves the page its own words.
 */
export function failureMessage(error: unknown): string | null {
  return error instanceof ApiError ? error.serverMessage : null;
}

/** The first name of the list already given to someone, or null. */
export function firstTakenName(names: string[]): string | null {
  const seen = new Set<string>();
  for (const name of names) {
    const key = normalizeName(name);
    if (seen.has(key)) {
      return name;
    }
    seen.add(key);
  }
  return null;
}

/** A PIN as the server takes it (FamilyMember::CODE_PATTERN): four digits */
export function isValidPin(pin: string): boolean {
  return /^\d{4}$/.test(pin);
}

/** What a PIN field keeps of what was typed: its first four digits */
export function pinDigits(text: string): string {
  return text.replace(/\D/g, "").slice(0, 4);
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** The date as the three boxes hold it, or null when they do not make one. */
export function birthDateOf(dob: TBirthDate): Date | null {
  const year = Number(dob.year);
  const month = Number(dob.month);
  const day = Number(dob.day);
  if (dob.year.length !== 4 || !year || !month || !day || year < 1900) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export function ageOn(birthDate: Date, today: Date): number {
  let age = today.getFullYear() - birthDate.getFullYear();
  const beforeBirthday =
    today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() < birthDate.getDate());
  if (beforeBirthday) {
    age -= 1;
  }
  return age;
}

/** What keeps the screen from moving on, or null when it may. */
export function validateStep(
  step: string,
  answers: TOnboardingAnswers,
  limits: TOnboardingLimits,
  today: Date = new Date(),
): OnboardingError | null {
  switch (step) {
    case "email":
      return isValidEmail(answers.email) ? null : "email";
    case "terms": {
      const birthDate = birthDateOf(answers.birthDate);
      if (birthDate === null || birthDate > today) {
        return "birth-date";
      }
      if (ageOn(birthDate, today) < limits.legalAge) {
        return "under-age";
      }
      return answers.consent ? null : "terms";
    }
    case "password":
      if (charCount(answers.password) < limits.minPassword) {
        return "password-short";
      }
      return answers.password === answers.passwordConfirm
        ? null
        : "password-mismatch";
    case "parentName":
      return hasValidNameLength(answers.parentName) ? null : "name-length";
    case "kidCount":
      return answers.kidCount >= 1 && answers.kidCount <= limits.maxChildren
        ? null
        : "children-count";
    case "kidNames": {
      if (!answers.kidNames.every(hasValidNameLength)) {
        return "name-length";
      }
      const taken = firstTakenName([...answers.kidNames, answers.parentName]);
      return taken === null ? null : "name-taken";
    }
    case "pin":
      return isValidPin(answers.pin) ? null : "pin";
    case "avatar":
      return answers.avatar !== null && answers.avatar !== "" ? null : "avatar";
    case "childName":
      return hasValidNameLength(answers.childName) ? null : "name-length";
    default:
      return null;
  }
}

function isoDate(dob: TBirthDate): string {
  return `${dob.year}-${dob.month.padStart(2, "0")}-${dob.day.padStart(2, "0")}`;
}

function legalConsent(answers: TOnboardingAnswers): TLegalConsent {
  return {
    birthDate: isoDate(answers.birthDate),
    acceptedTos: true,
    acceptedPrivacy: true,
  };
}

export function buildAccountPayload(
  answers: TOnboardingAnswers,
): TAccountPayload {
  return {
    account: { email: answers.email.trim(), password: answers.password },
    legal: legalConsent(answers),
  };
}

export function progressOf(
  current: string,
  history: string[],
  answers: TOnboardingAnswers,
): TServerProgress {
  return {
    current,
    history,
    answers: {
      who: answers.who,
      parentName: answers.parentName,
      kidCount: answers.kidCount,
      kidNames: answers.kidNames,
      avatar: answers.avatar,
      childName: answers.childName,
    },
  };
}

/** The child path names one child with its avatar; the parent path names them all, avatars later. */
function childrenOf(
  answers: TOnboardingAnswers,
): TOnboardingPayload["children"] {
  if (answers.who === "child") {
    const child: TOnboardingPayload["children"][number] = {
      name: answers.childName.trim(),
    };
    if (answers.avatar !== null) {
      child.avatar = answers.avatar;
    }
    return [child];
  }
  return answers.kidNames.map((name) => ({ name: name.trim() }));
}

export function buildPayload(
  answers: TOnboardingAnswers,
  mode: OnboardingMode,
  locale: string,
  completedAt: Date = new Date(),
): TOnboardingPayload {
  const payload: TOnboardingPayload = {
    startedBy: answers.who ?? "parent",
    account: {
      email: answers.email.trim(),
      authProvider: mode === "google" ? "google" : "password",
    },
    children: childrenOf(answers),
    meta: {
      completedAt: completedAt.toISOString(),
      locale,
    },
  };
  // A Google account still owes the consent; a password account gave it with the account
  if (mode === "google") {
    payload.legal = legalConsent(answers);
  }
  if (answers.who === "parent") {
    payload.parent = { name: answers.parentName.trim(), pin: answers.pin };
  }
  return payload;
}
